import { Injectable } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { AuthProviderService } from './auth-provider.interface';
import { Observable, tap, of, catchError, combineLatest, take } from 'rxjs';
import { clearUser, setUser } from '../store/user/user.actions';
import { SbvpRole, UserState } from '../store/user/user.state';
import { Store } from '@ngrx/store';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { AuthConfigService } from './auth-config.service';
import { AuthCleanupService } from './auth-cleanup.service';

@Injectable({ providedIn: 'root' })
export class OidcAuthService implements AuthProviderService {
  private _isAuthenticated = false;

  constructor(
    private oidcSecurityService: OidcSecurityService,
    private store: Store<{ user: UserState }>,
    private router: Router,
    private authConfigService: AuthConfigService,
    private authCleanupService: AuthCleanupService,
  ) {
    // Écouter l'état d'authentification de manière réactive
    this.oidcSecurityService.isAuthenticated$.subscribe(({ isAuthenticated }) => {
      this._isAuthenticated = isAuthenticated;
    });
  }

  public init(config?: any): Observable<any> {
    // On initialise le service de vérification d'authentification
    console.log('Initializing OIDC authentication service');

    try {
      // Vérifier l'état de l'authentification
      return this.oidcSecurityService.checkAuth().pipe(
        tap(({ isAuthenticated }) => {
          console.log('Auth check result:', isAuthenticated);
          if (isAuthenticated) {
            this.fetchAndSetUserProfile(); // update le store

            // Si l'utilisateur vient de se connecter, on le redirige vers l'URL configurée
            if (window.location.href.includes('code=') && window.location.href.includes('state=')) {
              console.log('Detected auth callback, redirecting to target URL');

              setTimeout(() => {
                // Vérifier s'il y a une URL stockée avant la redirection vers la page de login
                const requestedUrl = this.authConfigService.getAndClearRequestedUrl();
                if (requestedUrl) {
                  console.log('Redirecting to previously requested URL:', requestedUrl);
                  this.router.navigateByUrl(requestedUrl);
                } else {
                  // Sinon, redirection vers l'URL configurée dans l'environnement
                  const defaultRedirectPath = this.authConfigService.getPostLoginRedirectPath();
                  console.log('No stored URL, redirecting to default path:', defaultRedirectPath);
                  this.router.navigateByUrl(defaultRedirectPath);
                }
              }, 500);
            }
          } else {
            console.log('User not authenticated');
          }
        }),
      );
    } catch (error) {
      console.error('Error during OIDC initialization:', error);
      // Retourner un observable qui se termine correctement pour ne pas bloquer l'initialisation
      return of({ isAuthenticated: false });
    }
  }

  public async login(redirectUri?: string): Promise<void> {
    await this.oidcSecurityService.authorize(redirectUri);
  }

  public logout(redirectUri?: string): Observable<any> {
    console.log('OIDC logout called');

    // 1. Nettoyer le store NgRx et les données locales
    this.store.dispatch(clearUser());

    // 2. Déterminer l'URL de redirection post-logout
    const logoutRedirectUri =
      redirectUri || environment.auth.logoutRedirectUri || window.location.origin;

    // 3. Effectuer le logoff via la bibliothèque OIDC
    // logoff() redirige vers le end_session_endpoint de Keycloak avec les custom params
    return this.oidcSecurityService
      .logoff(undefined, {
        customParams: {
          client_id: environment.auth.oidc.clientId,
          post_logout_redirect_uri: logoutRedirectUri,
        },
      })
      .pipe(
        tap(() => {
          console.log('OIDC logoff successful');
          // Nettoyage complet après logoff
          this.authCleanupService.cleanupSession();
        }),
        catchError(error => {
          console.error('OIDC logout error, forcing manual logout:', error);

          // Nettoyage complet
          this.authCleanupService.cleanupSession();

          // Fallback : redirection manuelle vers l'endpoint de déconnexion Keycloak
          const logoutUrl =
            `${environment.auth.oidc.authority}/protocol/openid-connect/logout` +
            `?client_id=${encodeURIComponent(environment.auth.oidc.clientId)}` +
            `&post_logout_redirect_uri=${encodeURIComponent(logoutRedirectUri)}`;

          console.log('Fallback redirect to:', logoutUrl);
          window.location.href = logoutUrl;

          return of(true);
        }),
      );
  }

  public getUserInfo(): Promise<any> {
    return this.oidcSecurityService.getUserData().toPromise();
  }

  public get isAuthenticated(): boolean {
    return this._isAuthenticated;
  }

  public get getUserToken(): string {
    let token = '';
    this.oidcSecurityService
      .getAccessToken()
      .pipe(take(1))
      .subscribe(accessToken => (token = accessToken));
    return token;
  }

  public refreshToken(): Observable<any> {
    return this.oidcSecurityService.forceRefreshSession();
  }

  get isTokenExpired(): boolean {
    return !this.isAuthenticated;
  }

  /**
   * Décode un token JWT et retourne le payload
   */
  private decodeJwtToken(token: string): any {
    try {
      // Un JWT est composé de 3 parties séparées par des points: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.warn('[OIDC] Token JWT invalide: format incorrect');
        return null;
      }

      // Décoder le payload (partie 2)
      const payload = parts[1];

      // Ajouter le padding si nécessaire (base64url)
      let base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const pad = base64.length % 4;
      if (pad) {
        base64 += new Array(5 - pad).join('=');
      }

      // Décoder en JSON
      const decodedPayload = JSON.parse(atob(base64));
      return decodedPayload;
    } catch (error) {
      console.error('[OIDC] Erreur lors du décodage du token JWT:', error);
      return null;
    }
  }

  /**
   * Extrait les rôles depuis un token JWT décodé
   */
  private extractRolesFromToken(tokenPayload: any): string[] {
    const roles: string[] = [];

    if (!tokenPayload) {
      return roles;
    }

    // 1. Chercher dans resource_access (rôles au niveau application)
    if (tokenPayload.resource_access) {
      for (const clientId in tokenPayload.resource_access) {
        const clientRoles = tokenPayload.resource_access[clientId]?.roles || [];
        if (Array.isArray(clientRoles) && clientRoles.length > 0) {
          // Ajouter les rôles au tableau (éviter les doublons)
          clientRoles.forEach((role: string) => {
            if (typeof role === 'string' && !roles.includes(role)) {
              roles.push(role);
            }
          });
        }
      }
    }

    // 2. Chercher dans realm_access (rôles au niveau realm)
    if (tokenPayload.realm_access?.roles) {
      const realmRoles = tokenPayload.realm_access.roles;
      if (Array.isArray(realmRoles)) {
        realmRoles.forEach((role: string) => {
          if (typeof role === 'string' && !roles.includes(role)) {
            roles.push(role);
          }
        });
      }
    }

    return roles;
  }

  /**
   * Convertit une valeur Keycloak multivaluée (liste ou chaîne unique) en string[].
   * Retourne [] si la valeur est absente ou vide.
   */
  private toStringArray(value: any): string[] {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(v => typeof v === 'string' && v);
    if (typeof value === 'string' && value) return [value];
    return [];
  }

  /**
   * Extrait les groupes depuis un token JWT décodé
   */
  private extractGroupsFromToken(tokenPayload: any): string[] {
    const groups: string[] = [];

    if (!tokenPayload) {
      return groups;
    }

    // Chercher dans groups
    if (tokenPayload.groups && Array.isArray(tokenPayload.groups)) {
      tokenPayload.groups.forEach((group: string) => {
        if (typeof group === 'string' && !groups.includes(group)) {
          groups.push(group);
        }
      });
    }

    return groups;
  }

  public fetchAndSetUserProfile(): void {
    // Combiner les observables userData$ et getAccessToken() pour avoir les deux en même temps
    combineLatest([this.oidcSecurityService.userData$, this.oidcSecurityService.getAccessToken()])
      .pipe(
        take(1), // Prendre seulement la première valeur pour éviter les souscriptions multiples
      )
      .subscribe(([userData, token]: [any, string]) => {
        if (!userData || !userData.userData) {
          console.warn('[OIDC] Aucune donnée utilisateur disponible');
          return;
        }

        const profile = userData.userData;

        // Extraire les rôles et groupes depuis le token JWT
        let roles: string[] = [];
        let groups: string[] = [];

        if (token) {
          const tokenPayload = this.decodeJwtToken(token);
          if (tokenPayload) {
            roles = this.extractRolesFromToken(tokenPayload);
            groups = this.extractGroupsFromToken(tokenPayload);

            console.log('[OIDC] Token JWT décodé:', {
              resource_access: tokenPayload.resource_access,
              realm_access: tokenPayload.realm_access,
              groups: tokenPayload.groups,
              extracted_roles: roles,
              extracted_groups: groups,
            });
          }
        }

        // Si les rôles n'ont pas été extraits du token, essayer depuis le profile
        if (roles.length === 0) {
          // 1. Chercher dans resource_access (rôles au niveau application)
          if (profile.resource_access) {
            for (const clientId in profile.resource_access) {
              const clientRoles = profile.resource_access[clientId]?.roles || [];
              if (Array.isArray(clientRoles) && clientRoles.length > 0) {
                clientRoles.forEach((role: string) => {
                  if (typeof role === 'string' && !roles.includes(role)) {
                    roles.push(role);
                  }
                });
              }
            }
          }

          // 2. Si pas de rôles dans resource_access, chercher dans realm_access
          if (roles.length === 0 && profile.realm_access?.roles) {
            const realmRoles = profile.realm_access.roles;
            if (Array.isArray(realmRoles)) {
              realmRoles.forEach((role: string) => {
                if (typeof role === 'string' && !roles.includes(role)) {
                  roles.push(role);
                }
              });
            }
          }

          // 3. Si les rôles sont déjà dans profile.roles, les utiliser
          if (
            roles.length === 0 &&
            profile.roles &&
            Array.isArray(profile.roles) &&
            profile.roles.length > 0
          ) {
            profile.roles.forEach((role: any) => {
              const roleName = typeof role === 'string' ? role : role?.name || role?.id;
              if (roleName && typeof roleName === 'string' && !roles.includes(roleName)) {
                roles.push(roleName);
              }
            });
          }
        }

        // Si les groupes n'ont pas été extraits du token, essayer depuis le profile
        if (groups.length === 0 && profile.groups && Array.isArray(profile.groups)) {
          profile.groups.forEach((group: any) => {
            const groupName = typeof group === 'string' ? group : group?.name || group?.id;
            if (groupName && typeof groupName === 'string' && !groups.includes(groupName)) {
              groups.push(groupName);
            }
          });
        }

        // Extraire le rôle SBVP dominant depuis les rôles déjà collectés.
        // Priorité identique à celle du backend (jwt_role_reading_policy) :
        // resource_access > realm_access, déjà appliquée par extractRolesFromToken.
        const SBVP_ROLES: SbvpRole[] = [
          'admin_sbvp',
          'admin_application',
          'developer_application',
          'customer',
        ];
        const sbvpRole: SbvpRole | null = SBVP_ROLES.find(r => roles.includes(r)) ?? null;

        // Extraire les claims de données d'accès depuis le payload JWT déjà décodé.
        // Politique least-privilege : null uniquement pour admin_sbvp (accès total).
        // Pour tous les autres rôles, la liste est extraite du JWT ([] si absente).
        const isAdminSbvp = sbvpRole === 'admin_sbvp';
        const tokenPayload = token ? this.decodeJwtToken(token) : null;
        const allowedApplicationIds: string[] | null = isAdminSbvp
          ? null
          : this.toStringArray(tokenPayload?.['allowed_application_ids']);
        const allowedClientIds: string[] | null = isAdminSbvp
          ? null
          : this.toStringArray(tokenPayload?.['allowed_client_ids']);

        const userDataToStore = {
          ...profile,
          isLoggedIn: true,
          username: `${profile.given_name || ''} ${profile.family_name || ''}`.trim(),
          roles: roles, // Stocker explicitement les rôles extraits
          groups: groups, // Stocker explicitement les groupes extraits
          authorization: profile.authorization || [],
          sbvpRole,
          allowedApplicationIds,
          allowedClientIds,
        };

        console.log('[OIDC] User profile extracted:', {
          username: userDataToStore.username,
          email: userDataToStore.email,
          roles: userDataToStore.roles,
          groups: userDataToStore.groups,
          profile_resource_access: profile.resource_access,
          profile_realm_access: profile.realm_access,
          profile_groups: profile.groups,
        });

        // Stocker l'email de l'utilisateur dans sessionStorage (Dynatrace)
        if (userDataToStore.email) {
          window.sessionStorage.setItem('user_mail', userDataToStore.email);
          console.log('[OIDC] User email stored in sessionStorage:', userDataToStore.email);
        }

        this.store.dispatch(setUser(userDataToStore));
      });
  }
}
